import { Children, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The gallery's endless row, which runs by itself and can be taken by hand.
 *
 * It used to be a CSS keyframe, which nothing can grab. Now one offset drives
 * it: a frame loop adds to it while nobody is holding the row, and a mouse
 * drag or a sideways swipe moves it one to one under the finger. Letting go
 * leaves it where it was put, and the row picks its own pace back up two
 * seconds later. The track holds four copies of the pictures and the offset is
 * kept modulo one copy's width, so the row wraps without a seam either way.
 *
 * On a phone the row must never get in the way of scrolling the page. It is
 * `touch-action: pan-y`, so a vertical swipe is the browser's from the start,
 * and the row only claims a gesture once it has moved further sideways than
 * up or down. A drag also swallows the click that ends it, so letting go over
 * a picture does not count as choosing it.
 *
 * It moves the same with reduced motion, as the rest of the landing does (the
 * owner's decision), but it stops while it is off screen or the tab is hidden.
 */

/*
 * How fast the row runs, in pixels a second.
 *
 * It used to be a time for one whole copy of the pictures to cross, which
 * meant the speed changed with how many there were: adding to the gallery
 * made it race, and the work row, which holds four cards, crawled beside it.
 * A speed is the thing that should stay the same between the two rows and
 * across a row that grows.
 */
const PX_PER_SECOND = 57;
/** How long the row waits after it is let go before it runs again. */
const RESUME_MS = 2000;

/*
 * On a phone the row is not driven at all: it is an ordinary horizontal
 * scroller that snaps to each card, and it steps on by one card every few
 * seconds while nobody is touching it.
 *
 * The driven row asked a thumb to win an argument with an animation: the
 * row was moving under the finger, a sideways drag had to be told apart from
 * a scroll by a few pixels of slop, and a flick ended where the finger let go
 * rather than carrying on. The browser's own scroller has the momentum, the
 * snap and the edge bounce a phone user already knows, and the card at the
 * right edge showing half of itself is the only instruction it needs.
 */
const PHONE = '(max-width: 760px)';
const STEP_MS = 3200;
/** How long a touch, or any scroll the reader made, holds the stepping off. */
const HOLD_MS = 6000;
/** How far a press has to travel before it counts as a drag. */
const SLOP = 6;

export function GalleryTrack({
  copies,
  className = '',
  children,
}: {
  copies: number;
  /** A second class for a row that is not the gallery's, such as the work row. */
  className?: string;
  children: ReactNode;
}) {
  const track = useRef<HTMLDivElement>(null);
  // False while the page is being rendered ahead of time and while React
  // adopts that markup, so the two agree; a phone switches after that.
  const [phone, setPhone] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(PHONE);
    const read = () => setPhone(query.matches);
    read();
    query.addEventListener('change', read);
    return () => query.removeEventListener('change', read);
  }, []);

  // The phone's scroller: snapping, and one card further every few seconds.
  useEffect(() => {
    const el = track.current;
    if (!phone || !el) return;
    el.style.transform = '';
    el.scrollLeft = 0;
    let visible = false;
    let holdUntil = 0;
    let ownScrollUntil = 0;

    const step = () => {
      const card = el.firstElementChild as HTMLElement | null;
      if (!card) return;
      const stride = card.offsetWidth + (parseFloat(getComputedStyle(card).marginRight) || 0);
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      ownScrollUntil = performance.now() + 900;
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + stride, behavior: 'smooth' });
    };
    const timer = window.setInterval(() => {
      if (visible && !document.hidden && performance.now() >= holdUntil) step();
    }, STEP_MS);
    const hold = () => {
      holdUntil = performance.now() + HOLD_MS;
    };
    // A scroll this effect did not start is the reader's, and holds the stepping.
    const onScroll = () => {
      if (performance.now() > ownScrollUntil) hold();
    };
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
    });
    seen.observe(el);
    el.addEventListener('pointerdown', hold, { passive: true });
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearInterval(timer);
      seen.disconnect();
      el.removeEventListener('pointerdown', hold);
      el.removeEventListener('scroll', onScroll);
    };
  }, [phone]);

  useEffect(() => {
    const el = track.current;
    if (!el || phone) return;
    let period = el.scrollWidth / copies;
    let offset = 0;
    let last = 0;
    let frame = 0;
    let visible = true;
    let hovered = false;
    let resumeAt = 0;
    // The press being watched: where it started, and whether it has been claimed.
    let press: { id: number; x: number; y: number; from: number; dragging: boolean } | null = null;
    let swallowClick = false;

    const wrap = (value: number) => (period > 0 ? ((value % period) + period) % period : 0);
    const paint = () => {
      el.style.transform = `translate3d(${(-offset).toFixed(2)}px, 0, 0)`;
    };

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      const dt = last ? Math.min(now - last, 100) : 0;
      last = now;
      const running = !press?.dragging && !hovered && now >= resumeAt;
      if (running && period > 0) {
        offset = wrap(offset + PX_PER_SECOND * (dt / 1000));
        paint();
      }
    };
    const start = () => {
      if (frame || !visible || document.hidden) return;
      last = 0;
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };

    const onDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      press = { id: event.pointerId, x: event.clientX, y: event.clientY, from: offset, dragging: false };
      swallowClick = false;
    };
    const onMove = (event: PointerEvent) => {
      if (!press || event.pointerId !== press.id) return;
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      if (!press.dragging) {
        if (Math.abs(dy) > SLOP && Math.abs(dy) >= Math.abs(dx)) {
          // An up-and-down gesture: the page's scroll, not ours.
          press = null;
          return;
        }
        if (Math.abs(dx) <= SLOP) return;
        press.dragging = true;
        // Start from where the finger is now, so the row does not jump by the slop.
        press.x = event.clientX;
        press.from = offset;
        el.setPointerCapture(event.pointerId);
        el.dataset.dragging = 'true';
        return;
      }
      offset = wrap(press.from - dx);
      paint();
    };
    const onUp = (event: PointerEvent) => {
      if (!press || event.pointerId !== press.id) return;
      if (press.dragging) {
        swallowClick = true;
        resumeAt = performance.now() + RESUME_MS;
        // The mouse is still over the row after a drag; the pause it set ends with the drag.
        hovered = false;
        delete el.dataset.dragging;
        if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
      }
      press = null;
    };
    const onClick = (event: MouseEvent) => {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    };
    // A picture dragged by the mouse would otherwise start the browser's own image drag.
    const onDragStart = (event: DragEvent) => event.preventDefault();
    // A resting mouse pauses the row, as the keyframe's :hover did.
    const onEnter = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') hovered = true;
    };
    const onLeave = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      hovered = false;
      if (!press?.dragging) resumeAt = Math.max(resumeAt, performance.now());
    };

    const resize = new ResizeObserver(() => {
      period = el.scrollWidth / copies;
      offset = wrap(offset);
      paint();
    });
    resize.observe(el);
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true;
      if (visible) start();
      else stop();
    });
    seen.observe(el);
    const onVisibility = () => (document.hidden ? stop() : start());

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('click', onClick, true);
    el.addEventListener('dragstart', onDragStart);
    document.addEventListener('visibilitychange', onVisibility);
    paint();
    start();

    return () => {
      stop();
      resize.disconnect();
      seen.disconnect();
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('click', onClick, true);
      el.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [copies, phone]);

  // A scroller needs one copy of the pictures; the others exist only so the
  // driven row can wrap without a seam.
  const items = Children.toArray(children);
  const shown = phone ? items.slice(0, Math.ceil(items.length / copies)) : items;

  return (
    <div
      className={`lp-gallery-track ${className}`.trim()}
      data-mode={phone ? 'scroll' : undefined}
      ref={track}
    >
      {shown}
    </div>
  );
}
